import bytes from 'bytes';
import cx from 'classnames';
import {isEmpty, join, map} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useDropzone, DropzoneOptions} from 'react-dropzone';
import {useTranslation} from 'react-i18next';

import {TRANSLATION_KEYS} from 'locale';
import {fileSystemUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.DROPZONE;

type Props = {
    filesUploaded?: number;
    maxFiles?: number;
    options?: DropzoneOptions;
    onUpload: (files: {file: File, base64: string}[]) => void;
};

export const DropZone = ({filesUploaded = 0, maxFiles, options, onUpload}: Props) => {
    const {t} = useTranslation();

    const maxFilesExceeded = useMemo(() => filesUploaded >= maxFiles, [filesUploaded, maxFiles]);

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        const mappedFiles = [];
        if (!isEmpty(acceptedFiles)) {
            for (let i = 0; i < acceptedFiles.length; i++) {
                const file = acceptedFiles[i];
                const base64 = await fileSystemUtils.getFileAsBase64(file);
                mappedFiles.push({file, base64});
            }
        }
        onUpload(mappedFiles);
    }, [onUpload]);

    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        ...(options && options),
        onDrop
    });

    const label = useMemo(() => {
        if (isDragActive) {
            return t(T_KEYS.STATUS.DRAGGING);
        } else if (maxFilesExceeded) {
            return t(T_KEYS.STATUS.MAX_FILES_EXCEEDED);
        }
        return t(T_KEYS.STATUS.DEFAULT);
    }, [t, isDragActive, maxFilesExceeded]);

    return (
        <div {...getRootProps({
            className: cx(
                'tw-p-4 tw-text-default tw-flex-center tw-flex-col tw-min-h-16 tw-cursor-pointer',
                'tw-border-2 tw-border-primary-light tw-border-dashed tw-text-center', {'tw-bg-default': isDragActive})
        })}
        >
            <input {...getInputProps()} />
            {maxFiles &&
                <div className='tw-my-1 -sm:tw-hidden'>
                    {`${t(T_KEYS.HELPER.UPLOADED_FILES)}: ${filesUploaded} ${t(T_KEYS.HELPER.OF)} ${maxFiles}`}
                </div>}
            <div className='tw-mt-4 tw-mb-1 tw-ts-h5-bold tw-text-dark -sm:tw-hidden'>{label}</div>
            <div className='tw-mt-4 tw-mb-4 sm:tw-hidden'>
                <span className='tw-ts-base-bold tw-text-dark'>{label}</span>
                <span>{` (${t(T_KEYS.HELPER.UPLOADED_FILES)}: ${filesUploaded} ${t(T_KEYS.HELPER.OF)} ${maxFiles})`}</span>
            </div>
            {options?.accept &&
                <div className='tw-my-1'>
                    {`${t(T_KEYS.HELPER.EXTENSIONS)}: ${join(map(options.accept, (value) => `*${value}`), ', ')}`}
                </div>}
            {options?.maxSize &&
                <div className='tw-my-1'>
                    {`${t(T_KEYS.HELPER.MAX_SIZE)}: ${bytes(options.maxSize)} (${t(T_KEYS.HELPER.MAX_SIZE_REJECT)})`}
                </div>}
        </div>
    );
};